import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Clock, MapPin, Church } from 'lucide-react';

export default function NossosCultos() {
  const { data: services = [], isLoading } = useQuery({
    queryKey: ['worshipServices'],
    queryFn: () => base44.entities.WorshipService.filter({ is_active: true })
  });

  const dayOrder = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  
  const sortedServices = [...services].sort((a, b) => {
    return dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week);
  });

  const dayColors = {
    'Domingo': 'bg-indigo-500',
    'Segunda': 'bg-blue-500',
    'Terça': 'bg-emerald-500',
    'Quarta': 'bg-amber-500',
    'Quinta': 'bg-purple-500',
    'Sexta': 'bg-rose-500',
    'Sábado': 'bg-cyan-500',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Nossos Cultos" subtitle="Programação semanal" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {sortedServices.length === 0 ? (
          <EmptyState
            icon={Church}
            title="Nenhum culto cadastrado"
            description="Em breve teremos a programação de cultos."
          />
        ) : (
          <div className="space-y-4">
            {sortedServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="flex">
                  <div className={`${dayColors[service.day_of_week] || 'bg-gray-500'} w-2`} />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{service.title}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${dayColors[service.day_of_week] || 'bg-gray-500'}`} />
                            {service.day_of_week}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {service.time}
                          </span>
                        </div>
                        {service.location && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {service.location}
                          </div>
                        )}
                        {service.description && (
                          <p className="text-sm text-gray-500 mt-3">{service.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-200"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Church className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-amber-800">Venha nos visitar!</h4>
              <p className="text-sm text-amber-700 mt-1">
                Será uma alegria recebê-lo em nossa igreja. Todos são bem-vindos!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}